/**
 * Lock folder file crypto. Matches server AES-256-GCM layout:
 * R2 stores ciphertext only; IV and auth tag stored in Mongo metadata.
 *
 * `crypto.subtle` exists only in secure contexts (HTTPS or localhost).
 * On a LAN address such as http://192.168.x.x the browser still has
 * `crypto.getRandomValues`, but `subtle` is missing — use AES-GCM from
 * @noble/ciphers so lock uploads and previews keep working.
 */

import { gcm } from '@noble/ciphers/aes.js';
import {
  LOCK_ENC_FORMAT_CHUNK_V1,
  LOCK_CHUNK_PLAIN_BYTES,
  LOCK_GCM_TAG_BYTES,
  deriveChunkIvBytes,
  lockChunkCount,
} from './storageBoxLockChunkFormat';

const IV_LEN = 12;
const TAG_LEN = 16;
const KEY_LEN = 32;

function webCryptoSubtle() {
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (
    subtle &&
    typeof subtle.importKey === 'function' &&
    typeof subtle.encrypt === 'function' &&
    typeof subtle.decrypt === 'function'
  ) {
    return subtle;
  }
  return null;
}

function asUint8(data) {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  return new Uint8Array(data);
}

function asAesKey(masterKeyBytes) {
  const key = asUint8(masterKeyBytes);
  if (key.byteLength !== KEY_LEN) {
    throw new Error('Lock session expired. Unlock again.');
  }
  return key;
}

async function sealAesGcm(plainBuffer, masterKeyBytes, iv) {
  const subtle = webCryptoSubtle();
  if (subtle) {
    const key = await subtle.importKey('raw', masterKeyBytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    return new Uint8Array(
      await subtle.encrypt({ name: 'AES-GCM', iv, tagLength: TAG_LEN * 8 }, key, plainBuffer)
    );
  }
  return gcm(asAesKey(masterKeyBytes), iv).encrypt(asUint8(plainBuffer));
}

async function openAesGcm(combined, masterKeyBytes, iv) {
  const subtle = webCryptoSubtle();
  if (subtle) {
    const key = await subtle.importKey('raw', masterKeyBytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    return subtle.decrypt({ name: 'AES-GCM', iv, tagLength: TAG_LEN * 8 }, key, combined);
  }
  return gcm(asAesKey(masterKeyBytes), iv).decrypt(asUint8(combined));
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToB64(bytes) {
  let bin = '';
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  arr.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin);
}

export function parseMasterKeyB64(masterKeyB64) {
  if (!masterKeyB64) return null;
  return b64ToBytes(masterKeyB64);
}

export function clearMasterKeyBytes(keyBytes) {
  if (keyBytes && keyBytes.fill) keyBytes.fill(0);
}

export async function encryptFileBuffer(plainBuffer, masterKeyBytes) {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  const cipherWithTag = await sealAesGcm(plainBuffer, masterKeyBytes, iv);
  const tagStart = cipherWithTag.length - TAG_LEN;
  const ciphertext = cipherWithTag.slice(0, tagStart);
  const authTag = cipherWithTag.slice(tagStart);
  return {
    ciphertext,
    encIv: bytesToB64(iv),
    encAuthTag: bytesToB64(authTag),
  };
}

async function encryptPlainChunk(plainBuffer, masterKeyBytes, baseIvBytes, chunkIndex) {
  const iv = deriveChunkIvBytes(baseIvBytes, chunkIndex);
  const cipherWithTag = await sealAesGcm(plainBuffer, masterKeyBytes, iv);
  return cipherWithTag;
}

export async function encryptFile(file, masterKeyBytes) {
  const baseIv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  const encIv = bytesToB64(baseIv);
  const chunkPlain = LOCK_CHUNK_PLAIN_BYTES;
  const cipherParts = [];
  const totalPlain = file.size;
  const chunks = lockChunkCount(totalPlain, chunkPlain);
  for (let i = 0; i < chunks; i += 1) {
    const start = i * chunkPlain;
    const end = Math.min(start + chunkPlain, totalPlain);
    const slice = file.slice(start, end);
    const plain = await slice.arrayBuffer();
    const cipherWithTag = await encryptPlainChunk(plain, masterKeyBytes, baseIv, i);
    cipherParts.push(cipherWithTag);
  }
  const totalCipherLen = cipherParts.reduce((n, p) => n + p.length, 0);
  const ciphertext = new Uint8Array(totalCipherLen);
  let offset = 0;
  cipherParts.forEach((part) => {
    ciphertext.set(part, offset);
    offset += part.length;
  });
  return {
    ciphertext,
    encIv,
    encAuthTag: '',
    encFormat: LOCK_ENC_FORMAT_CHUNK_V1,
    encChunkPlainSize: chunkPlain,
    sizeBytes: file.size,
    name: file.name,
    mimeType: file.type || '',
  };
}

export async function decryptLockChunk(cipherWithTag, encIvB64, chunkIndex, masterKeyBytes) {
  const baseIv = b64ToBytes(encIvB64);
  const iv = deriveChunkIvBytes(baseIv, chunkIndex);
  const arr = asUint8(cipherWithTag);
  if (arr.length < LOCK_GCM_TAG_BYTES) {
    throw new Error('Invalid encrypted chunk');
  }
  const tag = arr.subarray(arr.length - LOCK_GCM_TAG_BYTES);
  const ct = arr.subarray(0, arr.length - LOCK_GCM_TAG_BYTES);
  const combined = new Uint8Array(ct.length + tag.length);
  combined.set(ct, 0);
  combined.set(tag, ct.length);
  return openAesGcm(combined, masterKeyBytes, iv);
}

export async function decryptToArrayBuffer(ciphertext, encIv, encAuthTag, masterKeyBytes) {
  const iv = b64ToBytes(encIv);
  const tag = b64ToBytes(encAuthTag);
  const ct =
    ciphertext instanceof ArrayBuffer
      ? new Uint8Array(ciphertext)
      : ciphertext instanceof Uint8Array
        ? ciphertext
        : new Uint8Array(ciphertext);
  const combined = new Uint8Array(ct.length + tag.length);
  combined.set(ct, 0);
  combined.set(tag, ct.length);
  return openAesGcm(combined, masterKeyBytes, iv);
}

export async function decryptToBlob(ciphertext, encIv, encAuthTag, masterKeyBytes, mimeType) {
  const plain = await decryptToArrayBuffer(ciphertext, encIv, encAuthTag, masterKeyBytes);
  return new Blob([plain], { type: mimeType || 'application/octet-stream' });
}

export async function decryptToText(ciphertext, encIv, encAuthTag, masterKeyBytes) {
  const plain = await decryptToArrayBuffer(ciphertext, encIv, encAuthTag, masterKeyBytes);
  return new TextDecoder('utf-8', { fatal: false }).decode(plain);
}
